/**
 * Dungeons (mockup docs/art/mockups/dungeon.jpg): tabs Mythic+ | Weekly | Rank.
 *
 *   Mythic+  one tall card per real dungeon (painted art, key stepper, target / best, affixes,
 *            reward slots, ENTER) + the Weekly Ritual bar. ⚙ on a card = run options.
 *   Weekly   Daily Offering, Weekly Ritual, Vault: a bar each (the vault and ritual are claimed
 *            automatically after a run, see run.ts autoClaimAfterRun).
 *   Rank     the tier crest, rating, sub-rank bar, the 5-tier ladder, the next unlock, best keys.
 *
 * Replaces the old Vault and Rank screens (the `vault` / `rank` route ids are aliases of the tabs).
 */
import type { AffixId } from '@keystone/sim';
import type { MetaScreenContext, MetaScreenFactory } from '../context';
import { AFFIX_OPTIONS } from '../hubPrefs';
import { standingKey } from '../badges';
import { localDateString } from '../forge/localDate';
import { RESOURCE_META } from '../forge/resourceText';
import { el } from '../ui/dom';
import { type KmIcon, classCrest, iconButton, iconTabs, kmIcon, kmModal, kmToast, panel, primaryButton, progressBar, roundIcon, secondaryButton, sideNav, stepper, toggle } from '../kmmo';
import { metaShell, resourceSlot } from '../kmmo/extra-lobby';
import { type DungeonCard, type DungeonsTab, TIER_COLOR, dungeonCards, dungeonsTab, lastVaultText, rankView, weeklyRows } from './dungeonsModel';
import './dungeons.css';
import { tutorialCard } from '../../tutorial/dungeonCard'; // +tutorial

const INFO = [
  'Pick a dungeon and a key level, then ENTER. Time a key to unlock the next level; your best timed key in each dungeon sets your rating.',
  'Affixes start at +4 (Fortified or Tyrannical) and stack at +10.',
  'Every run counts toward the Weekly Ritual (4 runs) and the Vault (5 runs). Both are claimed for you after the run that fills them.',
];

const AFFIX_ICON: Readonly<Record<string, KmIcon>> = { fortified: 'helm', tyrannical: 'crit' };
const AFFIX_NAME = (a: AffixId): string => a.charAt(0).toUpperCase() + a.slice(1);

function cardArt(id: string): string {
  const base = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
  return `${base}assets/ui/dungeons/${id}.jpg`;
}

export const dungeonsScreen: MetaScreenFactory = (ctx) => {
  const shell = metaShell(ctx, { title: 'Dungeons', info: () => INFO, className: 'kdg' });
  const body = shell.frame.body;
  let tab: DungeonsTab = dungeonsTab(ctx.params['tab']);
  let selected: string | null = ctx.prefs.get().dungeonId;
  if (tab === 'rank') ctx.prefs.set({ rankSeen: standingKey(ctx.store.profile) });

  const render = (): void => {
    body.replaceChildren();
    iconTabs(
      [
        { id: 'mplus', label: 'Mythic+' },
        { id: 'weekly', label: 'Weekly' },
        { id: 'rank', label: 'Rank' },
      ],
      {
        active: tab,
        style: 'box',
        onChange: (id) => {
          tab = id as DungeonsTab;
          if (tab === 'rank') ctx.prefs.set({ rankSeen: standingKey(ctx.store.profile) });
          render();
        },
      },
      el('div', 'kdg-tabs', body),
    );
    const view = el('div', `kdg-view kdg-${tab}`, body);
    if (tab === 'mplus') renderMplus(view);
    else if (tab === 'weekly') renderWeekly(ctx, view);
    else renderRank(ctx, view);
  };

  function renderMplus(view: HTMLElement): void {
    const cards = dungeonCards(ctx.store.profile, ctx.prefs.get(), ctx.content);
    if (!cards.some((c) => c.id === selected)) selected = cards[0]?.id ?? null;
    sideNav(
      cards.map((c) => ({ id: c.id, label: c.name.replace(/^The /, ''), icon: c.practice ? 'hammer' : 'dungeon' })),
      { active: selected ?? '', onChange: (id) => select(id) },
      el('nav', 'kdg-list', view),
    );
    const row = el('div', 'kdg-cards', view);
    for (const c of cards) dungeonCard(c, row);
    tutorialCard(ctx, row); // +tutorial: replay the guided run

    const w = weeklyRows(ctx.store.profile, localDateString()).find((r) => r.id === 'ritual')!;
    const bar = panel({ className: 'kdg-weekly' }, view);
    const head = el('div', 'kdg-weekly-head', bar.body);
    el('span', 'kdg-weekly-title', head, 'Weekly Ritual');
    el('span', 'kdg-weekly-note', head, w.status);
    progressBar({ value: w.value, max: w.max, tone: 'gold', value_text: 'fraction' }, bar.body).el.classList.add('kdg-weekly-bar');
    resourceSlot('sigils', 0, { size: 'md', title: 'Weekly Ritual: an Altar Sigil (or a Serpent’s Eye once your set is complete at Champion)' }, bar.body).el.classList.add('kdg-weekly-chest');
  }

  function select(id: string): void {
    if (selected === id) return;
    selected = id;
    ctx.prefs.set({ dungeonId: id });
    render();
  }

  function dungeonCard(c: DungeonCard, parent: HTMLElement): void {
    const on = c.id === selected;
    const card = el('article', `kdg-card km-ornate${on ? ' selected' : ''}${c.practice ? ' practice' : ''}`, parent);
    const art = el('div', 'kdg-art', card);
    art.style.setProperty('--art', `url("${cardArt(c.id)}")`);
    card.addEventListener('pointerdown', (e) => {
      if (!(e.target as HTMLElement).closest('button')) select(c.id);
    });

    el('h3', 'kdg-name', card, c.name);
    const meta = el('div', 'kdg-meta', card);
    const l = el('div', 'kdg-meta-l', meta);
    const k = el('div', '', l, 'Keystone ');
    el('b', 'kdg-key km-num', k, `+${c.level}`);
    el('div', 'kdg-best km-num', l, c.practice ? 'Practice · not rated' : `Best: ${c.best ?? '--:--'}`);
    const r = el('div', 'kdg-meta-r', meta, 'Target ');
    el('b', 'kdg-target km-num', r, c.target);

    stepper({
      value: c.level,
      min: c.min,
      max: c.max,
      format: (v) => `+${v}`,
      onChange: (v) => {
        ctx.prefs.set({ level: v });
        render();
      },
    }, el('div', 'kdg-step', card)).el.title = c.max > c.min ? `Unlocked up to +${c.max}` : 'Time a +2 to unlock +3';

    const aff = el('div', 'kdg-affixes', card);
    if (!c.affixes.length) el('span', 'kdg-noaffix', aff, 'No affixes below +4');
    for (const a of c.affixes) roundIcon(AFFIX_ICON[a] ?? 'diamond', { size: 'md', ring: 'gold', caption: AFFIX_NAME(a), title: AFFIX_NAME(a) }, aff);

    const rw = el('div', 'kdg-rewards', card);
    for (const it of c.rewards) {
      const cell = el('div', 'kdg-reward', rw);
      resourceSlot(it.key, it.n, { size: 'sm', title: `${RESOURCE_META[it.key]?.label ?? it.key} ×${it.n}` }, cell);
      el('span', 'kdg-reward-label', cell, RESOURCE_META[it.key]?.short ?? it.key);
    }

    const foot = el('div', 'kdg-foot', card);
    if (on) primaryButton('Enter', { onClick: () => start(c) }, foot);
    else secondaryButton('Enter', { onClick: () => select(c.id) }, foot);
    iconButton('settings', { framed: true, title: 'Run options', onClick: () => runOptions(ctx) }, foot).classList.add('kdg-opts');
  }

  function start(c: DungeonCard): void {
    ctx.prefs.set({ dungeonId: c.id, level: c.level });
    ctx.store.flush();
    const pr = ctx.prefs.get();
    ctx.startRun({ level: c.level, dungeonId: c.id, preset: ctx.store.profile.settings.defaultBotPreset, spectate: pr.spectate, affixes: AFFIX_OPTIONS[pr.affix]?.value ?? 'auto' });
  }

  render();
  const unsub = ctx.store.subscribe(render);
  return {
    el: shell.el,
    dispose() {
      unsub();
      shell.dispose();
    },
  };
};

function runOptions(ctx: MetaScreenContext): void {
  const m = kmModal('Run options');
  el('div', 'kdg-opt-label', m.body, 'Affixes');
  iconTabs(AFFIX_OPTIONS.map((o, i) => ({ id: String(i), label: o.label })), { active: String(ctx.prefs.get().affix), style: 'box', onChange: (id) => ctx.prefs.set({ affix: Number(id) }) }, m.body).el.classList.add('kdg-opt-tabs');
  toggle({ on: ctx.prefs.get().spectate, label: 'Watch only (bots play all four)', onChange: (on) => ctx.prefs.set({ spectate: on }) }, m.body).el.classList.add('kdg-opt-toggle');
  el('p', 'km-modal-text kdg-opt-note', m.body, 'Bot difficulty is under ⚙ Settings.');
  secondaryButton('Done', { onClick: () => m.close() }, m.footer);
}

const WEEKLY_ICON: Readonly<Record<string, KmIcon>> = { daily: 'chest', ritual: 'calendar', vault: 'gem' };
const WEEKLY_REWARD: Readonly<Record<string, string>> = { daily: 'sigils', ritual: 'sigils', vault: 'scale.jade' };

function renderWeekly(ctx: MetaScreenContext, view: HTMLElement): void {
  const p = ctx.store.profile;
  const box = panel({ className: 'kdg-weekly-panel' }, view);
  for (const r of weeklyRows(p, localDateString())) {
    const row = el('div', `kdg-wrow${r.ready ? ' ready' : ''}`, box.body);
    el('span', 'kdg-wrow-icon', row).appendChild(kmIcon(WEEKLY_ICON[r.id] ?? 'chest'));
    const mid = el('div', 'kdg-wrow-mid', row);
    const t = el('div', 'kdg-wrow-head', mid);
    el('span', 'kdg-wrow-title', t, r.title);
    el('span', 'kdg-wrow-status', t, r.status);
    progressBar({ value: r.value, max: r.max, tone: 'gold', value_text: 'fraction' }, mid);
    resourceSlot(WEEKLY_REWARD[r.id] ?? 'sigils', 0, { size: 'md' }, row);
  }
  const last = lastVaultText(ctx.prefs.get().lastVault);
  if (last) el('div', 'kdg-lastvault', box.body, last);
  if (p.vault.pending?.options.length) {
    secondaryButton('Claim the old vault item', {
      onClick: () => {
        void ctx.store.act('claimVault', { index: 0 }).then((r) => kmToast(r.ok ? 'Added to your bag' : r.reason, r.ok ? 'success' : 'error'));
      },
    }, box.body);
  }
}

function renderRank(ctx: MetaScreenContext, view: HTMLElement): void {
  const v = rankView(ctx.store.profile, ctx.content);
  const box = panel({ className: 'kdg-rank-panel' }, view);
  box.el.style.setProperty('--tier', TIER_COLOR[v.tier]);
  const top = el('div', 'kdg-rank-top', box.body);
  classCrest('rank', { icon: 'rank', size: 'lg', selected: true }, top);
  const tt = el('div', 'kdg-rank-text', top);
  el('div', 'kdg-rank-label', tt, v.label);
  el('div', 'kdg-rank-rating km-num', tt, v.rating.toLocaleString('en-US'));
  const bar = progressBar({ value: v.fill, max: 1, tone: 'gold' }, tt);
  bar.el.classList.add('kdg-rank-bar');
  el('div', 'kdg-rank-next', tt, v.toNext);

  const ladder = el('div', 'kdg-ladder', box.body);
  for (const t of v.ladder) {
    const c = classCrest('rank', { icon: t.state === 'locked' ? 'lock' : 'rank', label: t.name, size: 'sm', selected: t.state === 'current' }, ladder);
    c.classList.add(`kdg-tier-${t.state}`);
    c.style.setProperty('--tier', TIER_COLOR[t.tier]);
  }
  el('div', 'kdg-unlock', box.body, `Next unlock · ${v.nextUnlock}`);
  const rows = el('div', 'kdg-best-rows', box.body);
  for (const r of v.rows) {
    const row = el('div', 'kdg-best-row', rows);
    el('span', '', row, r.name);
    el('span', 'km-num', row, r.best);
  }
}
